/**
 * Kennung eines Prüfpunkts.
 *
 * Normalerweise die von der Datenbank vergebene Zahl. Ein offline ergänzter
 * Punkt hat die aber noch nicht – er trägt bis zur Synchronisierung eine
 * Client-UUID in der Form `local:<uuid>`. Deshalb beides.
 *
 * Bewusst KEINE negativen Zufallszahlen als Platzhalter: die kollidieren
 * früher oder später mit echten Ids, und der Fehler fällt erst auf, wenn
 * Daten schon vertauscht sind.
 */
export type PruefpunktId = number | string;

/** Dasselbe für Protokolleinträge – offline erzeugte tragen eine lokale Kennung. */
export type ProtokollId = number | string;

/** Ist das eine nur lokal vergebene Kennung (offline entstanden, noch nicht synchronisiert)? */
export function istLokaleId(id: PruefpunktId | ProtokollId | null | undefined): boolean {
  return typeof id === 'string' && id.startsWith('local:');
}

/** Erzeugt eine lokale Kennung für einen offline angelegten Datensatz. */
export function neueLokaleId(): string {
  return `local:${neueUuid()}`;
}

/**
 * UUID für Offline-Änderungen. `crypto.randomUUID` ist in modernen Browsern
 * vorhanden, fehlt aber in unsicheren Kontexten (HTTP ohne localhost) – dann
 * greift der Ersatz. Auf der Anlage läuft die Anwendung über HTTPS, der Fall
 * ist also die Ausnahme.
 */
export function neueUuid(): string {
  const c = (globalThis as any)?.crypto;

  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }

  if (c && typeof c.getRandomValues === 'function') {
    const bytes: Uint8Array = c.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b: number) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`;
}

// ── Listentyp und Anlagenkennzeichnung ─────────────────────────────────────

/**
 * Fachliche Art einer importierten Liste. Muss den Backend-Namen entsprechen
 * (JsonStringEnumConverter).
 *
 * `Unbekannt` ist ein gültiges Ergebnis und ausdrücklich besser als ein
 * erzwungener falscher Typ: die Liste wird trotzdem vollständig importiert,
 * nur ohne fachliche Feldzuordnung.
 */
export type ListenTyp = 'Unbekannt' | 'Messstellenliste' | 'Verbraucherliste';

/**
 * Art der Anlagenkennzeichnung. Der fachliche WERT liegt immer im Standardfeld
 * "Kennzeichnung"; diese Angabe sagt nur, wie das System heißt. Genau diese
 * Trennung erlaubt "SKS" statt pauschal "AKS/KKS" über der Spalte.
 */
export type KennzeichnungsArt =
  | 'Unbekannt'
  | 'KKS'
  | 'AKS'
  | 'AKZ'
  | 'SKS'
  | 'BMK'
  | 'Messkreisnummer'
  | 'TagNumber'
  | 'Sonstige';

/** Auswahl im Importdialog, in der Reihenfolge der Oberfläche. */
export const KENNZEICHNUNGS_ARTEN: { wert: KennzeichnungsArt; text: string }[] = [
  { wert: 'KKS', text: 'KKS' },
  { wert: 'AKS', text: 'AKS' },
  { wert: 'AKZ', text: 'AKZ' },
  { wert: 'SKS', text: 'SKS' },
  { wert: 'BMK', text: 'BMK' },
  { wert: 'Messkreisnummer', text: 'Messkreisnummer' },
  { wert: 'TagNumber', text: 'Tag Number' },
  { wert: 'Sonstige', text: 'Sonstige' },
  { wert: 'Unbekannt', text: 'Unbekannt' }
];

/** Auswahl des Listentyps im Importdialog. */
export const LISTEN_TYPEN: { wert: ListenTyp; text: string }[] = [
  { wert: 'Messstellenliste', text: 'Messstellenliste' },
  { wert: 'Verbraucherliste', text: 'Verbraucherliste' },
  { wert: 'Unbekannt', text: 'Unbekannt' }
];

/**
 * Was über der Kennzeichnungsspalte steht. Bei unbekannter Art bewusst das
 * neutrale "Kennzeichnung" – lieber unbestimmt als fachlich falsch.
 * Spiegelt `KennzeichnungsArten.Anzeige` im Backend.
 */
export function kennzeichnungLabel(art: KennzeichnungsArt | null | undefined): string {
  switch (art) {
    case 'KKS': return 'KKS';
    case 'AKS': return 'AKS';
    case 'AKZ': return 'AKZ';
    case 'SKS': return 'SKS';
    case 'BMK': return 'BMK';
    case 'Messkreisnummer': return 'Messkreisnummer';
    case 'TagNumber': return 'Tag Number';
    default: return 'Kennzeichnung';
  }
}

/** Ein Anlagenteil – einfache Gruppierung innerhalb einer Liste. */
export interface Anlagenteil {
  id: number;
  /** Anzeigename – beim Import entweder der Excel-Wert oder sein Klartext. */
  name: string;
  beschreibung?: string | null;
  sortOrder: number;
  /** Wie viele Messstellen der Liste (bzw. des Projekts, projektweit abgefragt) diesem Anlagenteil zugeordnet sind. */
  messstellenCount: number;
  /** Wie viele SPS-Listenzeilen des Projekts (Ort bzw. Path) diesen Anlagenteil tragen. */
  spsEintraegeCount?: number;

  /**
   * Die Excel-Rohwerte hinter dem Namen ("UKR12"). Nur zur Anzeige – klein
   * neben dem Klartext, damit die Herkunft nachvollziehbar bleibt. Leer, wenn
   * der Anlagenteil nicht aus einer Spalte stammt.
   */
  quellwerte?: string[] | null;
}

/** Kopfdaten einer importierten Messstellenliste (ohne die Messstellen selbst). */
export interface MessstellenListeSummary {
  id: number;
  customerId?: number | null;
  customerName?: string | null;
  /** Projekt, zu dem diese Inbetriebnahme gehört. Null bei Altbeständen. */
  projektId?: number | null;
  projektName?: string | null;
  projektNummer?: string | null;
  name: string;
  fileName?: string | null;
  sheetName?: string | null;
  importedAtUtc: string;
  importedByName?: string | null;
  columns: string[];
  /**
   * Standardfeld -> Spaltenname dieser Liste, aus der Strukturerkennung beim
   * Import. Die Tabellenansicht sortiert damit die fachlich wichtigen Spalten
   * nach vorne – angezeigt wird weiterhin der originale Excel-Spaltenname.
   */
  fieldMapping?: { [standardFeld: string]: string } | null;

  /**
   * Fachliche Art der Liste. Bestimmt, welcher Standardfeld-Katalog gilt.
   * Altbestände ohne gespeicherten Typ kommen als 'Messstellenliste' –
   * so verhalten sie sich exakt wie vor dem Umbau.
   */
  listenTyp?: ListenTyp | null;

  /** Kennzeichnungssystem der Liste (KKS, AKS, AKZ, SKS …). */
  kennzeichnungsArt?: KennzeichnungsArt | null;

  /** Fertige Beschriftung der Kennzeichnungsspalte, z. B. "SKS". */
  kennzeichnungLabel?: string | null;

  /**
   * Spaltenstruktur einer manuell erstellten Liste (Datentypen, Pflichtfelder,
   * Kennzeichnungsspalte). Null bei Excel-Importen.
   */
  struktur?: ListenStruktur | null;

  /** Die Anlagenteile dieser Liste – Grundlage für Filter und Auswahl. */
  anlagenteile?: Anlagenteil[] | null;

  messstellenCount: number;

  /** Prüfstand über alle Messstellen dieser Inbetriebnahme. */
  pruefpunkteGesamt: number;
  pruefpunkteErledigt: number;
  /** Wie viele Messstellen bereits eine Kategorie tragen. */
  messstellenMitKategorie: number;
  /** Wie viele Messstellen bereits mindestens einen Prüfpunkt haben. */
  messstellenMitPruefpunkten: number;
  /** Wie viele Messstellen vollständig abgearbeitet sind. */
  messstellenAbgeschlossen: number;
  /** Gesamtfortschritt; Messstellen ohne Prüfpunkte zählen mit 0 %. */
  fortschrittProzent: number;
}

/** Liste inklusive aller Messstellen. */
export interface MessstellenListeDetail extends MessstellenListeSummary {
  messstellen: Messstelle[];
}

/** Antwort auf das Duplizieren einer Messstelle – was die Kopie mitbekommen hat. */
export interface MessstelleDuplikatErgebnis {
  neueMessstelleId: number;
  pruefpunkte: number;
  verlaufseintraege: number;
  bilder: number;
  parameterstaende: number;
  zugangsdaten: number;
}

/**
 * Eine Messstelle = eine Zeile der Excel. Die Spalten sind je Liste unterschiedlich,
 * deshalb liegen die Werte als Spaltenname -> Wert vor und nicht als feste Felder.
 */
export interface Messstelle {
  id: number;
  rowNumber: number;
  values: { [column: string]: string };
  kategorieId?: number | null;
  kategorieName?: string | null;
  /** Anlagenteil, zu dem die Messstelle gehört. Null = keine Zuordnung. */
  anlagenteilId?: number | null;
  anlagenteilName?: string | null;
  /**
   * true, wenn die Zuordnung auf einen Anlagenteil außerhalb des Projekts
   * dieser Liste zeigt. Wird gekennzeichnet, nicht als gültige Auswahl geführt.
   */
  anlagenteilFremd?: boolean;
  /**
   * Prüffortschritt für die Listenansicht. "Erledigt" heißt: der Prüfpunkt
   * trägt ein Ergebnis ungleich "nicht geprüft" – also Bestanden,
   * NichtBestanden oder Irrelevant.
   */
  pruefpunkteGesamt: number;
  pruefpunkteErledigt: number;
  /** Workflow-Status. Kommt als Zahl ODER als Enum-Name vom Server. */
  signalStatus?: SignalStatus | string | null;
  /** Gesamtergebnis der Prüfung. Kommt als Zahl ODER als Enum-Name. */
  pruefergebnis?: Pruefergebnis | string | null;
  /** Änderungsstand – Grundlage der Konflikterkennung beim Offline-Abgleich. */
  version?: number;
}

// ── Import-Vorschau ──

export interface ExcelPreview {
  fileName: string;
  sheets: ExcelSheetPreview[];
}

export interface ExcelSheetPreview {
  name: string;
  /** Automatisch erkannte Kopfzeile (1-basiert). 0 = keine gefunden. */
  headerRowNumber: number;
  columns: string[];
  sampleRows: string[][];
  dataRowCount: number;
  /** Die ersten Zeilen roh – damit die Kopfzeile im Dialog korrigiert werden kann. */
  firstRows: ExcelRawRow[];
}

export interface ExcelRawRow {
  rowNumber: number;
  cells: string[];
}

// ── Inbetriebnahme: Kategorien und Prüfpunkte ──

/**
 * Bearbeitungsstand eines Prüfpunkts. ALTBESTAND – fachlich führend ist
 * `PruefpunktErgebnis`. Das Feld kommt weiterhin vom Server und wird dort aus
 * dem Ergebnis abgeleitet.
 */
export enum PruefpunktStatus {
  Offen = 0,
  Erledigt = 1,
  Irrelevant = 2
}

/**
 * Frühere fachliche Beurteilung. ALTBESTAND, siehe `PruefpunktStatus`.
 */
export enum PruefpunktBewertung {
  Keine = 0,
  InOrdnung = 1,
  NichtInOrdnung = 2
}

/**
 * Das Prüfergebnis eines Prüfpunkts – das führende Feld.
 * Zahlwerte entsprechen dem Backend-Enum.
 *
 * Für den Fortschritt gelten Bestanden, NichtBestanden und Irrelevant als
 * abgearbeitet; offen ist nur NichtGeprueft.
 */
export enum PruefpunktErgebnis {
  NichtGeprueft = 0,
  Bestanden = 1,
  NichtBestanden = 2,
  Irrelevant = 3
}

/**
 * Gesamtergebnis der Prüfung einer Messstelle. Bewusst getrennt vom
 * Fortschritt: 100 % abgearbeitet heißt nicht automatisch "bestanden".
 */
export enum Pruefergebnis {
  NichtGeprueft = 0,
  Bestanden = 1,
  NichtBestanden = 2
}

/**
 * Workflow-Status einer Messstelle.
 * Offen → In Bearbeitung → Getestet → Freigegeben,
 * bei Fehlern zurück über Fehler/Nacharbeit.
 */
export enum SignalStatus {
  Offen = 0,
  InBearbeitung = 1,
  Getestet = 2,
  Freigegeben = 3,
  FehlerNacharbeit = 4
}

/**
 * Art eines Eintrags im Aktivitätsverlauf.
 *
 * Werte unter 10 sind ENUM-Änderungen: hinter `ergebnis` steckt ein Enum, das
 * der Server beschriftet. Werte ab 10 sind WERT-Änderungen – dort steht der
 * lesbare Text in `altWert`/`neuWert`, und `ergebnis` bleibt 0.
 *
 * Muss wörtlich zu `PruefprotokollTyp` im Backend passen.
 */
export enum PruefprotokollTyp {
  PruefpunktErgebnis = 0,
  MessstellenErgebnis = 1,
  SignalStatus = 2,

  AllgemeineDaten = 10,
  PruefpunktAngelegt = 11,
  PruefpunktGeloescht = 12,
  PruefpunktBemerkung = 13,
  Kategorie = 14,
  Anlagenteil = 15,
  BildHinzugefuegt = 16,
  BildGeloescht = 17,
  BildBeschreibung = 18,

  /** Die allgemeine Notiz zur Messstelle – freiwillig, ohne Prüfbezug. */
  MessstelleNotiz = 19
}

/** Welche Art von Datensatz ein Eintrag betrifft. */
export enum ProtokollEntitaet {
  Messstelle = 0,
  Pruefpunkt = 1,
  Bild = 2,
  Anlagenteil = 3,
  Kategorie = 4
}

/** Gruppierung der Aktivitäten – die Auswahl im Aktivitätsfilter. */
export enum AktivitaetsGruppe {
  AllgemeineDaten = 1,
  Pruefpunkte = 2,
  Pruefergebnisse = 3,
  Status = 4,
  Kategorie = 5,
  Anlagenteil = 6,
  Bilder = 7,

  /** Freiwillige Notizen – heute die Notiz zur Messstelle. */
  Notizen = 8
}

/**
 * Gruppe eines Eintrags. Spiegelt `PruefungTexte.GruppeVon` im Backend – der
 * Verlauf wird offline lokal gefiltert, und dabei muss dieselbe Einteilung
 * herauskommen wie beim serverseitigen Filter.
 */
export function aktivitaetsGruppeVon(typ: unknown): AktivitaetsGruppe {
  switch (normalizePruefprotokollTyp(typ)) {
    case PruefprotokollTyp.AllgemeineDaten:
      return AktivitaetsGruppe.AllgemeineDaten;

    case PruefprotokollTyp.MessstelleNotiz:
      return AktivitaetsGruppe.Notizen;

    case PruefprotokollTyp.PruefpunktAngelegt:
    case PruefprotokollTyp.PruefpunktGeloescht:
      return AktivitaetsGruppe.Pruefpunkte;

    case PruefprotokollTyp.MessstellenErgebnis:
    case PruefprotokollTyp.SignalStatus:
      return AktivitaetsGruppe.Status;

    case PruefprotokollTyp.Kategorie:
      return AktivitaetsGruppe.Kategorie;

    case PruefprotokollTyp.Anlagenteil:
      return AktivitaetsGruppe.Anlagenteil;

    case PruefprotokollTyp.BildHinzugefuegt:
    case PruefprotokollTyp.BildGeloescht:
    case PruefprotokollTyp.BildBeschreibung:
      return AktivitaetsGruppe.Bilder;

    default:
      return AktivitaetsGruppe.Pruefergebnisse;
  }
}

/** Die Auswahl des Aktivitätsfilters, in der Reihenfolge der Oberfläche. */
export const AKTIVITAETS_GRUPPEN: {
  wert: AktivitaetsGruppe;
  text: string;
  icon: string;
}[] = [
  { wert: AktivitaetsGruppe.AllgemeineDaten, text: 'Allgemeine Daten', icon: 'edit_note' },
  { wert: AktivitaetsGruppe.Pruefpunkte, text: 'Prüfpunkte', icon: 'playlist_add' },
  { wert: AktivitaetsGruppe.Pruefergebnisse, text: 'Prüfergebnisse', icon: 'checklist' },
  { wert: AktivitaetsGruppe.Status, text: 'Status', icon: 'sync_alt' },
  { wert: AktivitaetsGruppe.Kategorie, text: 'Kategorie', icon: 'category' },
  { wert: AktivitaetsGruppe.Anlagenteil, text: 'Anlagenteil', icon: 'account_tree' },
  { wert: AktivitaetsGruppe.Bilder, text: 'Bilder', icon: 'photo_camera' },
  { wert: AktivitaetsGruppe.Notizen, text: 'Notizen', icon: 'sticky_note_2' }
];

/**
 * Überschrift eines Verlaufseintrags – WAS ist passiert.
 *
 * Das betroffene Feld bzw. der Prüfpunkt steht getrennt daneben, damit die
 * Überschrift kurz und über alle Einträge hinweg gleich bleibt.
 */
export function aktivitaetTitel(typ: unknown): string {
  switch (normalizePruefprotokollTyp(typ)) {
    case PruefprotokollTyp.SignalStatus:
      return 'Signalstatus geändert';

    case PruefprotokollTyp.MessstellenErgebnis:
      return 'Gesamtergebnis geändert';

    case PruefprotokollTyp.AllgemeineDaten:
      return 'Allgemeine Daten geändert';

    case PruefprotokollTyp.PruefpunktAngelegt:
      return 'Prüfpunkt angelegt';

    case PruefprotokollTyp.PruefpunktGeloescht:
      return 'Prüfpunkt gelöscht';

    case PruefprotokollTyp.PruefpunktBemerkung:
      return 'Prüfpunkt-Bemerkung geändert';

    case PruefprotokollTyp.Kategorie:
      return 'Kategorie geändert';

    case PruefprotokollTyp.Anlagenteil:
      return 'Anlagenteil geändert';

    case PruefprotokollTyp.BildHinzugefuegt:
      return 'Bild hinzugefügt';

    case PruefprotokollTyp.BildGeloescht:
      return 'Bild gelöscht';

    case PruefprotokollTyp.BildBeschreibung:
      return 'Bildbeschreibung geändert';

    case PruefprotokollTyp.MessstelleNotiz:
      return NOTIZ_TITEL.geaendert;

    default:
      return 'Prüfergebnis geändert';
  }
}

/** Die drei Überschriften einer Notiz-Aktivität. */
const NOTIZ_TITEL = {
  hinzugefuegt: 'Notiz zur Messstelle hinzugefügt',
  geaendert: 'Notiz zur Messstelle geändert',
  entfernt: 'Notiz zur Messstelle entfernt'
} as const;

/**
 * Überschrift eines Verlaufseintrags, aus dem EINTRAG statt nur aus seinem Typ.
 *
 * Nötig für die Notiz: ob sie hinzugefügt, geändert oder entfernt wurde, steht
 * nicht im Typ, sondern im Wertepaar `altWert → neuWert`. Bewusst dort
 * abgelesen und nicht zusätzlich gespeichert – sonst gäbe es zwei Wahrheiten
 * über denselben Vorgang.
 */
export function aktivitaetTitelVon(eintrag: {
  typ: unknown;
  altWert?: string | null;
  neuWert?: string | null;
}): string {
  if (
    normalizePruefprotokollTyp(eintrag.typ)
    !== PruefprotokollTyp.MessstelleNotiz
  ) {
    return aktivitaetTitel(eintrag.typ);
  }

  const vorher = (eintrag.altWert ?? '').trim();
  const nachher = (eintrag.neuWert ?? '').trim();

  if (!vorher && nachher) {
    return NOTIZ_TITEL.hinzugefuegt;
  }

  if (vorher && !nachher) {
    return NOTIZ_TITEL.entfernt;
  }

  return NOTIZ_TITEL.geaendert;
}

/** Icon je Aktivitätsart – hält den Verlauf auch beim Überfliegen lesbar. */
export function aktivitaetIcon(typ: unknown): string {
  switch (normalizePruefprotokollTyp(typ)) {
    case PruefprotokollTyp.SignalStatus:
      return 'sync_alt';

    case PruefprotokollTyp.MessstellenErgebnis:
      return 'fact_check';

    case PruefprotokollTyp.AllgemeineDaten:
      return 'edit_note';

    case PruefprotokollTyp.PruefpunktAngelegt:
      return 'playlist_add';

    case PruefprotokollTyp.PruefpunktGeloescht:
      return 'playlist_remove';

    case PruefprotokollTyp.PruefpunktBemerkung:
      return 'comment';

    case PruefprotokollTyp.Kategorie:
      return 'category';

    case PruefprotokollTyp.Anlagenteil:
      return 'account_tree';

    case PruefprotokollTyp.BildHinzugefuegt:
      return 'add_a_photo';

    case PruefprotokollTyp.BildGeloescht:
      return 'no_photography';

    case PruefprotokollTyp.BildBeschreibung:
      return 'photo_camera';

    case PruefprotokollTyp.MessstelleNotiz:
      return 'sticky_note_2';

    default:
      return 'checklist';
  }
}

/**
 * Worauf sich ein Verlaufseintrag bezieht: das geänderte Feld, der Prüfpunkt,
 * das Bild. Steht getrennt von der Überschrift, damit die über alle Einträge
 * hinweg gleich bleibt.
 */
export function aktivitaetBetreff(eintrag: {
  typ: unknown;
  feldName?: string | null;
  pruefpunktBezeichnung?: string | null;
}): string | null {
  switch (normalizePruefprotokollTyp(eintrag.typ)) {
    case PruefprotokollTyp.SignalStatus:
    case PruefprotokollTyp.MessstellenErgebnis:
    case PruefprotokollTyp.BildHinzugefuegt:
    case PruefprotokollTyp.BildGeloescht:
      return null;

    case PruefprotokollTyp.AllgemeineDaten:
    case PruefprotokollTyp.Kategorie:
    case PruefprotokollTyp.Anlagenteil:
    case PruefprotokollTyp.BildBeschreibung:
      return eintrag.feldName || null;

    // Die Überschrift nennt die Notiz bereits beim Namen.
    case PruefprotokollTyp.MessstelleNotiz:
      return null;

    default:
      return eintrag.pruefpunktBezeichnung || null;
  }
}

/** Ein Verlaufseintrag, fertig aufbereitet für die Anzeige. */
export interface AktivitaetAnzeige {
  /** WAS ist passiert, z. B. "Signalstatus geändert". */
  titel: string;

  /** Betroffenes Feld, Prüfpunkt oder Bild. */
  betreff: string | null;

  /** Zustand vor der Änderung – nur bei echten Wertepaaren. */
  vorherigerWert: string | null;

  /** Zustand nach der Änderung. */
  neuerWert: string | null;

  /** Ergänzender Hinweis, z. B. "Bereich: Funktionsprüfung". */
  zusatz: string | null;

  /** Freitext zum Vorgang. */
  bemerkung: string | null;
}

/**
 * Der EINE Weg vom Verlaufseintrag zur Anzeige – benutzt von der
 * Messstellen-Detailseite und vom IBN-PDF. Zwei eigene Aufbereitungen liefen
 * sonst früher oder später auseinander.
 *
 * Wie ein Ziel dargestellt wird (Pfeil, Farben, alter Zustand ja/nein),
 * entscheidet jede Ansicht selbst; WAS angezeigt wird, steht nur hier.
 */
export function formatAktivitaet(eintrag: {
  typ: unknown;
  altWert?: string | null;
  neuWert?: string | null;
  feldName?: string | null;
  pruefpunktBezeichnung?: string | null;
  ergebnisText?: string | null;
  vorherigesErgebnisText?: string | null;
  beschreibung?: string | null;
  bemerkung?: string | null;
}): AktivitaetAnzeige {
  const betreff = aktivitaetBetreff(eintrag);
  const vorherigerWert = (eintrag.vorherigesErgebnisText ?? '').trim() || null;
  let neuerWert = (eintrag.ergebnisText ?? '').trim() || null;

  // Bei angelegten/gelöschten Prüfpunkten ist der "neue Wert" nur der Name –
  // derselbe Text wie im Betreff. Zweimal untereinander hilft niemandem.
  if (!vorherigerWert && neuerWert && neuerWert === betreff) {
    neuerWert = null;
  }

  // Bei einer geänderten Prüfpunkt-Bemerkung ist die Bemerkung selbst der
  // neue Wert – sie stünde sonst ein zweites Mal darunter.
  let bemerkung = (eintrag.bemerkung ?? '').trim() || null;
  if (bemerkung && bemerkung === neuerWert) {
    bemerkung = null;
  }

  return {
    titel: aktivitaetTitelVon(eintrag),
    betreff,
    vorherigerWert,
    neuerWert,
    zusatz: (eintrag.beschreibung ?? '').trim() || null,
    bemerkung
  };
}

/**
 * Das Backend serialisiert Enums je nach Aufruf als Zahl ODER als Name
 * (`JsonStringEnumConverter`). Beides auf den Zahlwert bringen, damit die
 * Vergleiche im Template verlässlich sind.
 */
function normalizeEnum<T extends number>(
  value: unknown,
  enumObj: Record<string, unknown>,
  fallback: T
): T {

  if (typeof value === 'number' && enumObj[value] !== undefined) {
    return value as T;
  }

  if (typeof value === 'string') {

    // Enum-Name, z. B. "Bestanden".
    if (typeof enumObj[value] === 'number') {
      return enumObj[value] as T;
    }

    // Zahl als Text, z. B. "1".
    const zahl = Number(value);

    if (!Number.isNaN(zahl) && enumObj[zahl] !== undefined) {
      return zahl as T;
    }
  }

  return fallback;
}

export function normalizePruefpunktErgebnis(value: unknown): PruefpunktErgebnis {
  return normalizeEnum(
    value,
    PruefpunktErgebnis as unknown as Record<string, unknown>,
    PruefpunktErgebnis.NichtGeprueft
  );
}

export function normalizePruefergebnis(value: unknown): Pruefergebnis {
  return normalizeEnum(
    value,
    Pruefergebnis as unknown as Record<string, unknown>,
    Pruefergebnis.NichtGeprueft
  );
}

export function normalizeSignalStatus(value: unknown): SignalStatus {
  return normalizeEnum(
    value,
    SignalStatus as unknown as Record<string, unknown>,
    SignalStatus.Offen
  );
}

export function normalizePruefprotokollTyp(value: unknown): PruefprotokollTyp {
  return normalizeEnum(
    value,
    PruefprotokollTyp as unknown as Record<string, unknown>,
    PruefprotokollTyp.PruefpunktErgebnis
  );
}

/** Auswahl je Prüfpunkt, in der Reihenfolge der Oberfläche. */
export const PRUEFPUNKT_ERGEBNIS_OPTIONEN: {
  wert: PruefpunktErgebnis;
  text: string;
  icon: string;
  css: string;
}[] = [
  {
    wert: PruefpunktErgebnis.NichtGeprueft,
    text: 'Nicht geprüft',
    icon: 'radio_button_unchecked',
    css: 'ergebnis-nichtgeprueft'
  },
  {
    wert: PruefpunktErgebnis.Bestanden,
    text: 'Bestanden',
    icon: 'check_circle',
    css: 'ergebnis-bestanden'
  },
  {
    wert: PruefpunktErgebnis.NichtBestanden,
    text: 'Nicht bestanden',
    icon: 'cancel',
    css: 'ergebnis-nichtbestanden'
  },
  {
    wert: PruefpunktErgebnis.Irrelevant,
    text: 'Irrelevant',
    icon: 'block',
    css: 'ergebnis-irrelevant'
  }
];

/** Auswahl für das Gesamtergebnis einer Messstelle. */
export const PRUEFERGEBNIS_OPTIONEN: {
  wert: Pruefergebnis;
  text: string;
  css: string;
}[] = [
  {
    wert: Pruefergebnis.NichtGeprueft,
    text: 'Noch nicht geprüft',
    css: 'pruefergebnis-nichtgeprueft'
  },
  {
    wert: Pruefergebnis.Bestanden,
    text: 'Bestanden',
    css: 'pruefergebnis-bestanden'
  },
  {
    wert: Pruefergebnis.NichtBestanden,
    text: 'Nicht bestanden',
    css: 'pruefergebnis-nichtbestanden'
  }
];

/** Auswahl für den Signalstatus, in der Reihenfolge des typischen Ablaufs. */
export const SIGNAL_STATUS_OPTIONEN: {
  wert: SignalStatus;
  text: string;
  css: string;
}[] = [
  { wert: SignalStatus.Offen, text: 'Offen', css: 'signal-offen' },
  { wert: SignalStatus.InBearbeitung, text: 'In Bearbeitung', css: 'signal-inbearbeitung' },
  { wert: SignalStatus.Getestet, text: 'Getestet', css: 'signal-getestet' },
  { wert: SignalStatus.Freigegeben, text: 'Freigegeben', css: 'signal-freigegeben' },
  { wert: SignalStatus.FehlerNacharbeit, text: 'Fehler / Nacharbeit', css: 'signal-fehler' }
];

export function pruefpunktErgebnisText(ergebnis: unknown): string {
  const wert = normalizePruefpunktErgebnis(ergebnis);
  return PRUEFPUNKT_ERGEBNIS_OPTIONEN.find(o => o.wert === wert)?.text ?? 'Nicht geprüft';
}

export function pruefergebnisText(ergebnis: unknown): string {
  const wert = normalizePruefergebnis(ergebnis);
  return PRUEFERGEBNIS_OPTIONEN.find(o => o.wert === wert)?.text ?? 'Noch nicht geprüft';
}

export function signalStatusText(status: unknown): string {
  const wert = normalizeSignalStatus(status);
  return SIGNAL_STATUS_OPTIONEN.find(o => o.wert === wert)?.text ?? 'Offen';
}

export function signalStatusCss(status: unknown): string {
  const wert = normalizeSignalStatus(status);
  return SIGNAL_STATUS_OPTIONEN.find(o => o.wert === wert)?.css ?? 'signal-offen';
}

export function pruefergebnisCss(ergebnis: unknown): string {
  const wert = normalizePruefergebnis(ergebnis);
  return PRUEFERGEBNIS_OPTIONEN.find(o => o.wert === wert)?.css ?? 'pruefergebnis-nichtgeprueft';
}

/**
 * Art einer Messstelle – bestimmt die anzuwendenden Prüfpunkt-Vorlagen.
 *
 * Kommt samt Struktur vom Server (`GET inbetriebnahme/kategorien`), damit die
 * Kategorieauswahl eine Vorschau der Abschnitte und Prüfpunkte zeigen kann,
 * ohne beim Überfahren nachzuladen. Offline liefert der lokale Bestand
 * dieselbe Struktur.
 */
export interface MessstelleKategorie {
  id: number;
  name: string;
  beschreibung?: string | null;
  /** Farbe fuer die Darstellung, z. B. #1565c0. Optional. */
  color?: string | null;
  sortOrder: number;
  isActive: boolean;
  vorlagenCount: number;

  /** Abschnitte in Reihenfolge, jeder mit seinen Prüfpunkt-Vorlagen. */
  abschnitte?: MessstelleKategorieAbschnitt[];
  /** Vorlagen ohne Abschnitt (Altbestand) – werden unter „Allgemein“ geführt. */
  vorlagenOhneAbschnitt?: MessstelleKategorieVorlage[];
}

/** Ein Abschnitt der Prüfstruktur einer Kategorie. */
export interface MessstelleKategorieAbschnitt {
  id: number;
  name: string;
  sortOrder: number;
  vorlagen: MessstelleKategorieVorlage[];
}

/** Eine Prüfpunkt-Vorlage – daraus entsteht der konkrete Prüfpunkt. */
export interface MessstelleKategorieVorlage {
  id: number;
  abschnittId?: number | null;
  bezeichnung: string;
  sortOrder: number;
}

/** Ein konkreter Prüfpunkt an einer Messstelle. */
export interface MessstellePruefpunkt {
  /** Server-Id, oder `local:<uuid>` bei einem offline ergänzten Punkt. */
  id: PruefpunktId;
  /** null = von Hand ergänzt, stammt aus keiner Vorlage. */
  vorlageId?: number | null;
  section: string;
  bezeichnung: string;
  sortOrder: number;
  /** Führendes Prüfergebnis. Kommt als Zahl ODER als Enum-Name vom Server. */
  ergebnis: PruefpunktErgebnis;
  /** ALTBESTAND, vom Server aus `ergebnis` abgeleitet. */
  status?: PruefpunktStatus;
  /** ALTBESTAND, vom Server aus `ergebnis` abgeleitet. */
  bewertung?: PruefpunktBewertung;
  /** Bemerkung – bei "nicht bestanden" die verpflichtende Fehlerbeschreibung. */
  bemerkung?: string | null;
  quittiertAmUtc?: string | null;
  quittiertVon?: string | null;

  /**
   * Änderungsstand. Wird bei einer offline erfassten Änderung als
   * `baseVersion` zurückgeschickt; weicht er vom Server ab, hat inzwischen
   * jemand anderes geschrieben und es gibt eine Rückfrage statt eines stillen
   * Überschreibens.
   */
  version?: number;
  updatedAtUtc?: string | null;

  /** Client-Kennung eines offline angelegten Punkts. Sonst null. */
  clientId?: string | null;

  /**
   * Nur lokal: die Änderung liegt in der Warteschlange und ist noch nicht
   * beim Server. Wird nie übertragen.
   */
  nichtSynchronisiert?: boolean;
}

/**
 * Ein Eintrag des Prüfverlaufs: WER hat WANN WELCHES Ergebnis gesetzt.
 *
 * Rein lesend – die Einträge entstehen serverseitig und werden nie
 * überschrieben. `ergebnisText`/`vorherigesErgebnisText` kommen fertig
 * beschriftet vom Server, weil erst `typ` verrät, welches Enum hinter der
 * Zahl steckt.
 */
export interface MessstellePruefprotokoll {
  /** Server-Id, oder `local:<uuid>` bei einem offline entstandenen Eintrag. */
  id: ProtokollId;
  messstelleId: number;
  pruefpunktId?: PruefpunktId | null;
  pruefpunktBezeichnung?: string | null;
  benutzerId?: string | null;
  benutzerName?: string | null;
  zeitpunktUtc: string;
  typ: PruefprotokollTyp;
  ergebnis: number;
  vorherigesErgebnis?: number | null;
  ergebnisText: string;
  vorherigesErgebnisText?: string | null;
  bemerkung?: string | null;

  /**
   * Wann die Änderung den Server erreicht hat. Nur bei offline erfassten
   * Prüfungen gesetzt – dort weicht sie vom Prüfzeitpunkt ab (geprüft 10:31,
   * synchronisiert 13:02). `zeitpunktUtc` bleibt immer der Prüfzeitpunkt.
   */
  synchronisiertAmUtc?: string | null;

  /** Nur lokal: der Eintrag wartet noch auf die Synchronisierung. */
  nichtSynchronisiert?: boolean;

  // ── Aktivitätsverlauf ──

  /** Gruppe für Anzeige und Filter. Fehlt bei Einträgen älterer Server. */
  gruppe?: AktivitaetsGruppe | null;

  entityTyp?: ProtokollEntitaet | null;
  entityId?: number | null;

  /** Betroffenes Standardfeld bei einer Feldänderung, z. B. "Messbereich Max". */
  feldName?: string | null;

  /**
   * Roher alter bzw. neuer Wert. Für die Anzeige reichen
   * `vorherigesErgebnisText`/`ergebnisText` – die kommen fertig beschriftet
   * vom Server und decken auch die Enum-Einträge ab. Diese beiden Felder
   * dienen der Suche und dem Offline-Filter.
   */
  altWert?: string | null;
  neuWert?: string | null;

  /** Ergänzender Hinweis, z. B. "Bereich: Funktionsprüfung". */
  beschreibung?: string | null;
}

/**
 * Ein Ausschnitt des Aktivitätsverlaufs samt allem, was die Filterleiste
 * braucht. Antwort von `GET messstellen/{id}/aktivitaeten`.
 */
export interface AktivitaetsVerlauf {
  eintraege: MessstellePruefprotokoll[];

  /** Wie viele Einträge der Filter insgesamt trifft. */
  gesamt: number;

  /** Gibt es hinter dem gelieferten Ausschnitt noch mehr? */
  hatMehr: boolean;

  /** Alle Personen, die an dieser Messstelle je etwas geändert haben. */
  personen: string[];
}

/**
 * Ein Foto zu einer Messstelle – nur die Kopfdaten.
 *
 * Die Bilddaten selbst liegen in der Datenbank und werden einzeln über
 * `GET inbetriebnahme/bilder/{id}` geholt. Ein `<img src>` kann das nicht,
 * weil der Bearer-Token nur an HttpClient-Aufrufe angehängt wird – die
 * Detailseite lädt die Bilder deshalb als Blob und baut daraus Object-URLs.
 */
export interface MessstelleBild {
  id: number;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  beschreibung?: string | null;
  uploadedAtUtc: string;
  uploadedByName?: string | null;
}

/** Eine Messstelle mit allem, was die Detailseite braucht. */
export interface MessstelleDetail {
  id: number;
  rowNumber: number;
  messstellenListeId: number;
  messstellenListeName: string;
  customerName?: string | null;
  /** Projekt der Messstelle – ergibt sich aus der Messstellenliste. */
  projektId?: number | null;
  projektName?: string | null;
  projektNummer?: string | null;
  /** Spalten in Original-Reihenfolge – `values` selbst ist ungeordnet. */
  columns: string[];
  values: { [column: string]: string };
  /**
   * Standardfeld -> Spaltenname dieser Liste, aus der Strukturerkennung beim Import.
   * Damit findet die Seite z. B. "Hersteller", egal wie die Spalte in der Datei heißt.
   * Leer bei manuell importierten Listen.
   */
  fieldMapping?: { [standardFeld: string]: string } | null;
  /**
   * Spaltenname -> 'CORE' | 'ADDITIONAL', wie beim Import bestätigt.
   * Grundlage für den Abschnitt "Weitere relevante Daten" – dort erscheinen
   * auch Spalten, für die es gar kein Standardfeld gibt. Leer bei älteren Listen.
   */
  columnClassifications?: { [column: string]: SpaltenKlassifikation } | null;

  /**
   * Fachliche Art der Liste. Entscheidet, welche Standardfelder die
   * Detailseite anbietet – eine Verbraucherliste zeigt Nennleistung und
   * Motortyp, keine Messbereiche.
   */
  listenTyp?: ListenTyp | null;
  kennzeichnungsArt?: KennzeichnungsArt | null;
  /** Fertige Beschriftung für das Kennzeichnungsfeld, z. B. "SKS". */
  kennzeichnungLabel?: string | null;
  /** Spaltenstruktur der Liste bei manuell erstellten Listen; null bei Importen. */
  struktur?: ListenStruktur | null;

  kategorieId?: number | null;
  kategorieName?: string | null;

  /** Anlagenteil der Messstelle. Null = keine Zuordnung. */
  anlagenteilId?: number | null;
  anlagenteilName?: string | null;
  /**
   * true, wenn anlagenteilId auf einen Anlagenteil außerhalb des Projekts
   * dieser Messstelle zeigt. Er steht dann nicht in `anlagenteile`; die
   * Detailseite zeigt ihn als Hinweis und übernimmt ihn nicht als Auswahl.
   */
  anlagenteilFremd?: boolean;
  /**
   * Alle Anlagenteile des Projekts der Messstelle (ohne Projekt: der Liste),
   * alphabetisch – die Auswahl auf der Detailseite.
   */
  anlagenteile?: Anlagenteil[] | null;
  /** Gesamtergebnis der Prüfung. Kommt als Zahl ODER als Enum-Name. */
  pruefergebnis: Pruefergebnis;
  /** Bemerkung; bei "nicht bestanden" die verpflichtende Fehlerbeschreibung. */
  pruefergebnisBemerkung?: string | null;
  /** Workflow-Status. Kommt als Zahl ODER als Enum-Name. */
  signalStatus: SignalStatus;
  pruefpunkte: MessstellePruefpunkt[];
  /** Vollständiger Prüfverlauf, neueste Einträge zuerst. */
  pruefprotokoll: MessstellePruefprotokoll[];
  /** Fotos zur Messstelle, neueste zuerst. Ohne Bilddaten. */
  bilder: MessstelleBild[];
  canEdit: boolean;

  /** Änderungsstand der Messstelle – Grundlage der Konflikterkennung. */
  version?: number;
  updatedAtUtc?: string | null;
}

/**
 * Standardfeld -> Excel-Spalte dieser Liste.
 *
 * Spiegelt `MessstelleWerteAnwender.FindeSpalte` im Backend. Wird offline
 * gebraucht, weil dort niemand fragen kann, wohin ein bearbeitetes Feld
 * gehört – und beim späteren Abgleich muss dieselbe Spalte herauskommen.
 */
export function findeSpalte(
  standardFeld: string,
  fieldMapping: { [feld: string]: string } | null | undefined,
  columns: string[] | null | undefined
): string | null {
  const mapping = fieldMapping ?? {};
  const spalten = columns ?? [];

  for (const name of messstelleFeldMitAliassen(standardFeld)) {
    const gemappt = mapping[name];

    if (gemappt && gemappt.trim()) {
      return gemappt;
    }

    if (spalten.includes(name)) {
      return name;
    }
  }

  return null;
}

/**
 * Spaltenname für ein Feld, das die Datei noch nicht hatte. Spiegelt
 * `MessstelleWerteAnwender.EindeutigerSpaltenName`.
 */
export function eindeutigerSpaltenName(
  wunsch: string,
  columns: string[] | null | undefined
): string {
  const spalten = (columns ?? []).map(c => c.toLowerCase());

  if (!spalten.includes(wunsch.toLowerCase())) {
    return wunsch;
  }

  for (let i = 2; i < 100; i++) {
    const kandidat = `${wunsch} (${i})`;

    if (!spalten.includes(kandidat.toLowerCase())) {
      return kandidat;
    }
  }

  return `${wunsch} (${neueUuid().replace(/-/g, '')})`;
}

/** Nach `section` gruppierte Prüfpunkte – im Component gebaut, nicht im Template. */
export interface PruefpunktSection {
  name: string;
  punkte: MessstellePruefpunkt[];
  gesamt: number;
  erledigt: number;
}

// ── KI-gestützte Strukturerkennung beim Import ──

/**
 * Standardfelder, auf die Excel-Spalten abgebildet werden.
 * Spiegelt MessstelleStandardFelder im Backend – auch die Reihenfolge, denn
 * sie ist zugleich die fachliche Priorität für die Tabellenansicht.
 */
/** Das Feld, das eine Zeile fachlich benennt – in beiden Listentypen dasselbe. */
export const FELD_KENNZEICHNUNG = 'Kennzeichnung';

export const MESSSTELLE_STANDARDFELDER: string[] = [
  FELD_KENNZEICHNUNG,
  'Langtext',
  'Kurztext',
  'P&ID Schema',
  'Medium',
  'Signal',
  'Signalart',
  'Hilfsspannung',
  'Funktion',

  'Messbereich',
  'Messbereich Min',
  'Messbereich Max',
  'Messbereich Einheit',

  'Warnung Tief',
  'Warnung Hoch',
  'Alarm Tief',
  'Alarm Hoch',

  'Kalibrierung',
  'Sensor Hersteller',
  'Sensor Typ'
];

/**
 * Früher verwendete Feldnamen je aktuellem Feld.
 *
 * Listen, die vor der Umbenennung importiert wurden, tragen in ihrem
 * fieldMapping noch die alten Namen. Statt zu migrieren, sucht die Anzeige
 * zuerst nach dem aktuellen Namen und danach nach den Aliassen.
 * Spiegelt MessstelleStandardFelder.Aliase im Backend.
 */
export const MESSSTELLE_FELD_ALIASE: { [feld: string]: string[] } = {
  /*
   * "AKS/KKS" ist der wichtigste Eintrag: JEDE vor dem Umbau importierte
   * Liste hat ihn in ihrem fieldMapping stehen. Ohne diesen Alias liesse
   * sich keine Bestandsliste mehr anzeigen.
   */
  [FELD_KENNZEICHNUNG]: [
    'AKS/KKS',
    'AKS Nummer',
    'AKS-Nummer',
    'AKS',
    'KKS',
    'AKZ',
    'SKS',
    'BMK',
    'Messkreisnummer',
    'Tag Number'
  ],
  'Langtext': ['Bezeichnung'],
  'Signalart': ['Signaltyp'],
  'Sensor Hersteller': ['Hersteller'],
  'Sensor Typ': ['Typ']
};

/**
 * Standardfelder einer VERBRAUCHERLISTE.
 *
 * Abgeleitet aus realen Verbraucherlisten: dort beschreibt eine Zeile keinen
 * Messwert, sondern ein elektrisches Betriebsmittel. Ein Messstellen-Mapping
 * läge hier durchgehend daneben.
 * Spiegelt VerbraucherStandardFelder im Backend – auch die Reihenfolge.
 */
export const VERBRAUCHER_STANDARDFELDER: string[] = [
  FELD_KENNZEICHNUNG,
  'Langtext',
  'Kurztext',
  'P&ID Schema',
  'Anlagenbereich',
  'Medium',

  'Verbraucherart',
  'Antriebsart',
  'Netzart',

  'Nennspannung',
  'Nennleistung',
  'Nennstrom',
  'Anlaufstrom',
  'Nenndrehzahl',
  'Leistungsfaktor',
  'Wirkungsgrad',

  'Betriebsart',
  'Anlaufart',
  'Schutzart',

  'Motor Hersteller',
  'Motor Typ',

  'Frequenzumrichter',
  'FU Hersteller',
  'FU Typ',

  'Steuerung',
  'DI',
  'DO',
  'AI',
  'AO',

  'Lieferant',
  'Bemerkung'
];

export const VERBRAUCHER_FELD_ALIASE: { [feld: string]: string[] } = {
  [FELD_KENNZEICHNUNG]: MESSSTELLE_FELD_ALIASE[FELD_KENNZEICHNUNG],
  'Langtext': ['Bezeichnung'],
  'Leistungsfaktor': ['cos phi', 'cos φ'],
  'Motor Hersteller': ['Hersteller', 'Motorfabrikat'],
  'Motor Typ': ['Typ', 'Motortyp']
};

/**
 * Die Standardfelder des Listentyps, in fachlicher Priorität.
 *
 * Ohne Typ (Altbestand) gilt der Messstellen-Katalog – das war bis zur
 * Typerkennung der einzige unterstützte Fall.
 */
export function standardFelder(typ: ListenTyp | null | undefined): string[] {
  return typ === 'Verbraucherliste'
    ? VERBRAUCHER_STANDARDFELDER
    : MESSSTELLE_STANDARDFELDER;
}

/**
 * Bringt einen Feldnamen auf den aktuellen Katalognamen des Listentyps.
 *
 * Gebraucht wird das überall dort, wo ein Feldname von aussen kommt: aus der
 * Antwort des Servers, aus einer vor dem Umbau importierten Liste, aus einem
 * zwischengespeicherten Stand. Ein Auswahlfeld zeigt LEER, sobald der Wert
 * nicht exakt einer Option entspricht – und beim Speichern ginge die
 * Zuordnung dann still verloren. Genau das darf nicht passieren.
 *
 * Rückgabe:
 * - der Katalogname, wenn der Wert direkt oder über einen Alias passt
 *   ("AKS/KKS" → "Kennzeichnung", "Hersteller" → "Sensor Hersteller")
 * - sonst der unveränderte Wert – lieber unbekannt sichtbar lassen als
 *   stillschweigend verwerfen
 * - null nur, wenn nichts angegeben war
 */
export function normalisiereStandardFeld(
  feld: string | null | undefined,
  typ: ListenTyp | null | undefined
): string | null {
  const gesucht = (feld ?? '').trim();

  if (!gesucht) {
    return null;
  }

  const katalog = standardFelder(typ);

  const direkt = katalog.find(
    f => f.toLowerCase() === gesucht.toLowerCase());

  if (direkt) {
    return direkt;
  }

  const ueberAlias = katalog.find(
    f => messstelleFeldMitAliassen(f, typ)
      .some(name => name.toLowerCase() === gesucht.toLowerCase()));

  return ueberAlias ?? gesucht;
}

/**
 * Aktueller Feldname plus Aliasse, in Suchreihenfolge.
 *
 * Ohne Listentyp werden die Aliasse BEIDER Kataloge geliefert. Das ist für
 * die Anzeige richtig: eine alte Liste weiß nicht, welchen Typ sie hat, und
 * darf ihren Wert trotzdem finden.
 */
export function messstelleFeldMitAliassen(
  feld: string,
  typ?: ListenTyp | null
): string[] {
  const quellen = typ === 'Verbraucherliste'
    ? [VERBRAUCHER_FELD_ALIASE]
    : typ === 'Messstellenliste'
      ? [MESSSTELLE_FELD_ALIASE]
      : [MESSSTELLE_FELD_ALIASE, VERBRAUCHER_FELD_ALIASE];

  const ergebnis = [feld];

  for (const quelle of quellen) {
    for (const alias of quelle[feld] ?? []) {
      if (!ergebnis.some(v => v.toLowerCase() === alias.toLowerCase())) {
        ergebnis.push(alias);
      }
    }
  }

  return ergebnis;
}

/**
 * Was mit einer Excel-Spalte beim Import geschehen ist. Muss den
 * Backend-Namen entsprechen (JsonStringEnumConverter).
 *
 * 'IGNORE' heisst seit dem Umbau **ausgeschlossen**: die Spalte wurde gar
 * nicht importiert. (Früher hiess es „importiert, aber nachrangig“ – ältere
 * Listen tragen den Wert noch in dieser alten Bedeutung.)
 *
 * Im Importdialog kommt das nicht vor: dort entscheidet allein `isIncluded`.
 * Der Wert wird vom Server daraus abgeleitet.
 */
export type SpaltenKlassifikation = 'CORE' | 'ADDITIONAL' | 'IGNORE';

export interface ExcelAiColumn {
  /** 0-basiert, ab der ersten genutzten Spalte des Blatts. */
  columnIndex: number;
  originalName: string;
  /** Ein Standardfeld des erkannten Listentyps, oder null. */
  mappedField?: string | null;

  /**
   * Wird die Spalte importiert?
   *
   * Die endgültige Entscheidung des Benutzers: abgewählt heisst weder
   * anzeigen noch speichern. Weder die Werte noch ein Standardfeld, das nur
   * an dieser Spalte hängt, landen in der Datenbank.
   *
   * Die KI-Strukturerkennung läuft VORHER und sieht immer alle Spalten –
   * Listentyp, Kennzeichnungsart und semantische Feldzuordnung entstehen auf
   * dem vollständigen Blatt. Und während des Imports rechnen Kategorie- und
   * Anlagenteilregeln noch auf den vollständigen Excel-Werten: was eine Regel
   * ergeben hat, bleibt, auch wenn ihre Quellspalte nicht gespeichert wird.
   */
  isIncluded: boolean;

  /** Speicherform von `isIncluded` für die Detailseite. Vom Server gesetzt. */
  classification?: SpaltenKlassifikation;
  /** 0..1 – niedrige Werte sind ein Prüfhinweis. */
  confidence: number;
  /** Nur bei der Kennzeichnungsspalte gesetzt. */
  identifierType?: KennzeichnungsArt | null;
  /** Kurzer Beleg der KI. Rein informativ. */
  evidence?: string | null;
}

/** Die Beurteilung EINES Arbeitsblatts. */
export interface ExcelAiSheetAnalysis {
  sheetName: string;
  listType: ListenTyp;
  listTypeConfidence: number;
  identifierType: KennzeichnungsArt;
  identifierTypeConfidence: number;
  /** 0-basierter Index der Kennzeichnungsspalte, -1 = keine. */
  identifierColumn?: number;
  /** 0-basierter Index einer Spalte, die sich als Anlagenteil eignet. */
  suggestedPlantPartColumn?: number;

  /**
   * Die Spalten, an denen sich entscheidet, OB eine Zeile überhaupt ein
   * Datensatz ist – 0-basierte Indizes. Grundlage der Zeilenprüfung.
   */
  relevanceColumns?: number[] | null;
  /** Enthält das Blatt überhaupt eine importierbare Tabelle? */
  relevant: boolean;
  headerStartRow: number;
  headerEndRow: number;
  dataStartRow: number;
  columns: ExcelAiColumn[];
  reasoning?: string | null;
  /** Hinweise der deterministischen Nachvalidierung. */
  warnungen?: string[] | null;
}

/** Erkannte Struktur – vor dem Import vom Nutzer korrigierbar. */
export interface ExcelAiAnalysis {
  sheetName: string;
  headerStartRow: number;
  headerEndRow: number;
  dataStartRow: number;
  columns: ExcelAiColumn[];
  reasoning?: string | null;

  listType: ListenTyp;
  listTypeConfidence: number;
  identifierType: KennzeichnungsArt;
  identifierTypeConfidence: number;

  /** 0-basierter Index der Kennzeichnungsspalte, -1 = keine. */
  identifierColumn?: number;

  /**
   * 0-basierter Index einer Spalte, die sich als Anlagenteil eignet.
   * -1 = keine gefunden.
   */
  suggestedPlantPartColumn?: number;

  /**
   * Die Spalten, an denen sich entscheidet, OB eine Zeile ein Datensatz ist.
   * Grundlage der Zeilenprüfung – und der Grund, warum sie bezahlbar bleibt.
   */
  relevanceColumns?: number[] | null;

  /** Alle beurteilten Blätter – auch die nicht gewählten. */
  sheets?: ExcelAiSheetAnalysis[] | null;

  warnungen?: string[] | null;
}

/**
 * Vorschlag der KI, welche Messstellen zu einer BESTEHENDEN Kategorie passen –
 * als nachvollziehbare Filterregel, nicht als fertige Zuordnung.
 */
export interface KategorieVorschlag {
  kategorieName: string;
  feld: string;
  operator: RegelOperator;
  wert: string;
  confidence: number;
}

export interface ExcelAnalyseResult {
  preview: ExcelPreview;
  /** Null, wenn die Erkennung nicht möglich war – dann manuell weiter. */
  analysis?: ExcelAiAnalysis | null;
  aiAvailable: boolean;
  aiMessage?: string | null;
  kategorieVorschlaege?: KategorieVorschlag[] | null;
}

// ── Regelbasierte Zuordnung ────────────────────────────────────────────────

export type RegelOperator =
  | 'beginntMit'
  | 'enthaelt'
  | 'endetMit'
  | 'istGleich'
  | 'enthaeltNicht';

export const REGEL_OPERATOREN: { wert: RegelOperator; text: string }[] = [
  { wert: 'enthaelt', text: 'enthält' },
  { wert: 'beginntMit', text: 'beginnt mit' },
  { wert: 'endetMit', text: 'endet mit' },
  { wert: 'istGleich', text: 'ist gleich' },
  { wert: 'enthaeltNicht', text: 'enthält nicht' }
];

// ── Regeln, die beim Import angewendet werden ──

export interface KategorieRegel {
  feld: string;
  operator: RegelOperator;
  wert: string;
  kategorieId: number | null;
}

/**
 * Eine Anlagenteil-Regel spricht den Anlagenteil über den NAMEN an: vor dem
 * Import gibt es ihn noch nicht. Er entsteht erst beim Speichern, und zwar
 * genau dann, wenn die Regel auch getroffen hat.
 */
export interface AnlagenteilRegel {
  feld: string;
  operator: RegelOperator;
  wert: string;
  anlagenteilName: string;
}

export interface ImportRegeln {
  /** 0-basierter Spaltenindex, aus dessen Werten Anlagenteile entstehen. */
  anlagenteilSpalte: number | null;

  /**
   * Excel-Rohwert -> Anzeigename ("UKR12" -> "Absackung").
   *
   * Der Rohwert bleibt in der Messstelle unangetastet; hier entsteht nur der
   * Name, unter dem der Anlagenteil angelegt wird. Ein leerer Eintrag heißt
   * ausdrücklich "kein Klartext" – dann gilt der Rohwert selbst. Deshalb
   * werden auch leere Einträge mitgeschickt: sie unterscheiden „bewusst
   * gelöscht“ von „noch nichts gesagt“, und nur beim zweiten darf der Server
   * eine im Projekt bekannte Benennung ergänzen.
   */
  anlagenteilAnzeigenamen: { [excelWert: string]: string };

  anlagenteile: AnlagenteilRegel[];
  kategorien: KategorieRegel[];
}

// ── Vollständige Vorschau vor dem Import ──

export interface ImportVorschauSpalte {
  name: string;

  /**
   * 0-basierter Spaltenindex der Datei – derselbe Index wie in
   * `ExcelAiColumn.columnIndex`. Damit lässt sich ein Häkchen aus „Spalten
   * anpassen“ sofort auf die Vorschau anwenden, ohne auf die nächste
   * Server-Antwort zu warten.
   */
  columnIndex: number;

  anzeige: string;
  herkunft?: string | null;
  standardFeld?: string | null;

  /**
   * Wird die Spalte importiert? Abgewählte stehen bewusst MIT in dieser
   * Liste – „Spalten anpassen“ baut daraus seine Auswahl auf und braucht
   * dafür jede Spalte des Blatts.
   */
  isIncluded: boolean;

  /** Speicherform von `isIncluded` für die Detailseite. */
  classification?: SpaltenKlassifikation;
}

export interface ImportVorschauZeile {
  rowNumber: number;
  werte: { [column: string]: string };
  kategorieName?: string | null;
  anlagenteilName?: string | null;
}

/**
 * Was der Import ergeben WIRD – vollständig gerechnet, aber nichts
 * gespeichert. Entsteht auf demselben Weg wie der spätere Import.
 */
export interface ImportVorschau {
  sheetName: string;
  listenTyp: ListenTyp;
  kennzeichnungsArt: KennzeichnungsArt;
  kennzeichnungLabel: string;
  spalten: ImportVorschauSpalte[];
  zeilen: ImportVorschauZeile[];
  zeilenGesamt: number;
  mitKategorie: number;
  mitAnlagenteil: number;
  kategorieTreffer: number[];
  anlagenteilTreffer: number[];
  neueAnlagenteile: string[];

  /**
   * Die eindeutigen Werte der gewählten Anlagenteil-Spalte mit ihrem
   * Anzeigenamen – Grundlage der Übersetzungstabelle im Importdialog.
   */
  anlagenteilWerte?: AnlagenteilWert[] | null;

  warnungen: string[];
}

/** Ein Wert der Anlagenteil-Spalte und der Name, unter dem er erscheinen wird. */
export interface AnlagenteilWert {
  /** Der Wert, wie er in der Excel-Datei steht ("UKR12"). */
  wert: string;

  /** Der Anzeigename. Ohne eigene Angabe der Wert selbst. */
  anzeigename: string;

  /** Wie viele Zeilen diesen Wert tragen. */
  anzahl: number;

  /**
   * Der Anzeigename stammt aus einer früheren Inbetriebnahme desselben
   * Projekts. Ändern darf der Benutzer ihn trotzdem.
   */
  ausProjekt?: boolean;
}

/** Fortschritt eines Anlagenteils über ein ganzes Projekt. */
export interface AnlagenteilFortschritt {
  id: number;
  name: string;
  sortOrder: number;
  messstellenGesamt: number;
  messstellenGestartet: number;
  messstellenAbgeschlossen: number;
  pruefpunkteGesamt: number;
  pruefpunkteErledigt: number;
  fortschrittProzent: number;
}

/**
 * Die Anlagenteile EINER Inbetriebnahme mit ihrem Fortschritt.
 *
 * Die Gruppierung kommt fertig vom Server und ist fachlich notwendig, nicht
 * nur Optik: derselbe Anlagenteil kann in zwei Inbetriebnahmen desselben
 * Projekts vorkommen und dort ganz verschieden weit sein. Zugeordnet wird
 * über `inbetriebnahmeId`, nie über den Namen – zwei Listen dürfen gleich
 * heißen.
 */
export interface InbetriebnahmeAnlagenteile {
  /** Id der Messstellenliste (= Inbetriebnahme). */
  inbetriebnahmeId: number;
  inbetriebnahmeName: string;

  /** Alle aktuellen Messstellen dieser Inbetriebnahme. */
  messstellenGesamt: number;

  /** Messstellen dieser Inbetriebnahme ohne Anlagenteil. */
  messstellenOhneAnlagenteil: number;

  /** Nur Anlagenteile mit mindestens einer aktuellen Messstelle. */
  anlagenteile: AnlagenteilFortschritt[];
}

/** Ergebnis einer Regelanwendung bzw. ihrer Vorschau. */
export interface RegelErgebnis {
  treffer: number;
  geaendert: number;
  uebersprungen: number;
  beispiele: string[];
  anlagenteil?: Anlagenteil | null;
}

// ── Sammelzuordnung an einer bestehenden Liste ─────────────────────────────

/**
 * Dieselben Regeln wie beim Import, auf eine bereits importierte Liste
 * angewendet. Die erste zutreffende Regel gewinnt.
 */
export interface SammelZuordnungRegeln {
  kategorien: KategorieRegel[];
  anlagenteile: AnlagenteilRegel[];
}

export interface SammelZuordnungBeispiel {
  name: string;
  /** Die bisherige Zuordnung, die ersetzt wird. */
  bisher?: string | null;
}

export interface SammelRegelErgebnis {
  /** Wie viele Messstellen die Bedingung erfüllen. */
  treffer: number;
  /** Wie viele davon durch genau diese Regel geändert werden. */
  aenderungen: number;
  beispiele: SammelZuordnungBeispiel[];
}

export interface SammelZuordnungErgebnis {
  /** In der Reihenfolge der übergebenen Regeln. */
  kategorieRegeln: SammelRegelErgebnis[];
  anlagenteilRegeln: SammelRegelErgebnis[];
  /** Messstellen, deren Zuordnung sich ändert. */
  betroffen: number;
  /** Davon mit einer bereits vorhandenen, anderen Zuordnung. */
  ueberschrieben: number;
  /** Getroffen, aber bereits genau so zugeordnet. */
  unveraendert: number;
  /** Tatsächlich geschrieben – bei der Vorschau 0. */
  geaendert: number;
  neueAnlagenteile: string[];
  warnungen: string[];
}

// ── Abgleich mit einer neueren Excel-Datei ─────────────────────────────────

export type AbgleichArt =
  | 'Neu'
  | 'Geaendert'
  | 'Entfallen'
  | 'Unveraendert'
  | 'Unsicher'
  /**
   * Steht in der Datei, wurde beim Import aber bewusst ausgeschlossen – und
   * ist seitdem unverändert. Erscheint deshalb NICHT als „neu“.
   */
  | 'Ausgeschlossen'
  /**
   * Zeile ohne verwertbare Kennzeichnung: Gruppenkopf, Summen-, Zubehör-
   * oder Hilfszeile. Sie wird nicht verglichen und erzeugt keine
   * Messstellenänderung.
   */
  | 'OhneKennzeichnung';

/** Was sich am AUFBAU der Datei geändert hat – nicht an einer Zeile. */
export type StrukturAenderungArt =
  | 'NeueSpalte'
  | 'EntfalleneSpalte'
  | 'SpalteUmbenannt';

/**
 * Eine Änderung am Aufbau der Datei.
 *
 * Sie steht EINMAL im Bericht. Eine neue Spalte ist ein Ereignis für die
 * Liste – nicht eines je Messstelle.
 */
export interface StrukturAenderung {
  art: StrukturAenderungArt;
  spalte: string;
  bisherigeSpalte?: string | null;
  /** Wie viele Zeilen in dieser Spalte etwas stehen haben. */
  betroffeneZeilen: number;
  /** Umbenennungen sind reine Information – es gibt nichts zu übernehmen. */
  uebernehmbar: boolean;
}

// ── Zeilenrelevanz: soll die Zeile überhaupt importiert werden? ─────────────

/**
 * Reale Listen enthalten neben den Datensätzen Gruppenköpfe, Summenzeilen,
 * mechanische Bauteile und Bemerkungen. `Unsicher` ist bewusst kein Makel,
 * sondern die ehrliche Antwort – solche Zeilen legt die Vorschau ausdrücklich
 * vor, statt sie stillschweigend wegzuwerfen.
 */
export type ZeilenRelevanz = 'Relevant' | 'Irrelevant' | 'Unsicher';

/** Wer die Entscheidung getroffen hat. */
export type RelevanzQuelle = 'KI' | 'Regel' | 'Benutzer' | 'Uebernommen';

/** Das Urteil zu einer einzelnen Excel-Zeile. */
export interface ZeilenRelevanzEintrag {
  rowNumber: number;
  kennzeichnung?: string | null;
  fingerprint: string;
  relevanz: ZeilenRelevanz;
  grund?: string | null;
  quelle: RelevanzQuelle;
  manuellUeberschrieben: boolean;
  confidence: number;

  /** Die Werte der Spalten, auf die sich die Entscheidung stützt. */
  merkmale: { [spalte: string]: string };
}

/** Das Ergebnis der Zeilenprüfung über die ganze Liste. */
export interface ZeilenRelevanzErgebnis {
  zeilen: ZeilenRelevanzEintrag[];
  gesamt: number;
  relevant: number;
  irrelevant: number;
  unsicher: number;

  /** Die Spalten, auf die sich die Beurteilung stützt. */
  merkmalsspalten: string[];

  /** Wie viele Zeilen tatsächlich an die KI gingen, in wie vielen Anfragen. */
  kiZeilen: number;
  kiAnfragen: number;
  /** Wie viele Zeilen ohne KI entschieden wurden. */
  ohneKi: number;
  kiVerwendet: boolean;
  kiMeldung?: string | null;

  warnungen: string[];
}

/** Eine Entscheidung, wie sie der Client zurückschickt. */
export interface ZeilenEntscheidung {
  rowNumber: number;
  kennzeichnung?: string | null;
  fingerprint: string;
  relevanz: ZeilenRelevanz;
  grund?: string | null;
  quelle: RelevanzQuelle;
  manuellUeberschrieben: boolean;
}

export interface AbgleichFeld {
  spalte: string;
  alt?: string | null;
  neu?: string | null;
}

export interface AbgleichEintrag {
  art: AbgleichArt;
  kennzeichnung: string;
  anzeigename: string;
  messstelleId?: number | null;
  neueZeile?: number | null;
  neueWerte: { [column: string]: string };
  unterschiede: AbgleichFeld[];

  /** Prüfsumme über die entscheidungsrelevanten Felder dieser Zeile. */
  fingerprint?: string | null;

  /** Warum die Zeile beim letzten Import ausgeschlossen wurde. */
  ausschlussGrund?: string | null;

  /**
   * An dieser Zeile hat sich etwas geändert, das die Relevanz kippen könnte.
   * Ein geänderter Messbereich löst das nicht aus, ein geänderter Langtext schon.
   */
  relevanzPruefungNoetig?: boolean;
}

export interface AbgleichErgebnis {
  messstellenListeId: number;
  fileName: string;
  sheetName: string;
  listenTyp: ListenTyp;
  kennzeichnungsArt: KennzeichnungsArt;
  /**
   * Die Spalte der neuen Datei, über die zugeordnet wurde – die Kennzeichnung,
   * oder eine vom Benutzer gewählte Vergleichsspalte.
   */
  vergleichsSpalte?: string | null;

  /** Die Bestandsspalte, gegen die verglichen wurde. Darf anders heißen. */
  vergleichsSpalteBestand?: string | null;

  /**
   * Es liess sich keine Kennzeichnung bestimmen. Dann wird NICHT über
   * Zeilennummern geraten – die Zeilen bleiben unsicher, und der Benutzer
   * wählt eine Vergleichsspalte aus `vergleichsSpaltenVorschlaege`.
   */
  matchingUnsicher?: boolean;

  /** Spalten der neuen Datei zur Auswahl, wenn `matchingUnsicher` gilt. */
  vergleichsSpaltenVorschlaege?: string[] | null;

  /** Kennzeichnungen, die auf einer der beiden Seiten mehrfach vorkommen. */
  mehrfachVergeben?: string[] | null;

  /** Spalten, die es in der Bestandsliste noch nicht gibt. */
  neueSpalten: string[];

  /**
   * Spalten, die die Bestandsliste führt und die neue Datei nicht mehr.
   * Sie werden gemeldet, aber nie geleert.
   */
  entfalleneSpalten: string[];

  /**
   * Die Strukturänderungen als benannte Liste. Gegenpol zu `eintraege`: was
   * hier steht, betrifft die ganze Liste und taucht bei keiner einzelnen
   * Messstelle noch einmal auf.
   */
  strukturaenderungen: StrukturAenderung[];

  eintraege: AbgleichEintrag[];
  anzahlNeu: number;
  anzahlGeaendert: number;
  anzahlEntfallen: number;
  anzahlUnveraendert: number;
  anzahlUnsicher: number;

  /**
   * Zeilen ohne verwertbare Kennzeichnung – auf beiden Seiten. Gruppenköpfe,
   * Summen- und Zubehörzeilen. Sie werden nicht verglichen.
   */
  anzahlOhneKennzeichnung?: number;

  /**
   * Zeilen, die schon beim letzten Import bewusst weggelassen wurden und
   * seitdem unverändert sind. Sie erscheinen nicht als „neu“.
   */
  anzahlAusgeschlossen?: number;

  /**
   * Zeilen, an denen sich ein entscheidungsrelevantes Feld geändert hat – nur
   * sie müssten überhaupt erneut beurteilt werden.
   */
  anzahlRelevanzPruefung?: number;

  /** Die neue Datei ist inhaltlich deckungsgleich mit dem Bestand. */
  ohneUnterschied: boolean;

  warnungen: string[];
}

/**
 * Was von einem Abgleich übernommen werden soll.
 *
 * Die drei Ebenen sind getrennt, weil sie getrennt entschieden werden:
 * ganze Zeilen, einzelne Felder, und der Aufbau der Datei.
 *
 * „Alle Änderungen übernehmen“ füllt nur `neueZeilen` und
 * `aktualisierteMessstellen` und lässt `feldaenderungen` leer – ein leerer
 * Filter heisst serverseitig „alles, was gemeldet wurde“.
 */
export interface AbgleichAuswahl {
  neueZeilen: number[];
  aktualisierteMessstellen: number[];

  /** Nur gesetzt, wenn im Bearbeitungsmodus einzelne Felder abgewählt wurden. */
  feldaenderungen?: { messstelleId: number; spalten: string[] }[];

  /** Neue Spalten, die die Liste übernehmen soll. */
  neueSpalten?: string[];

  /** Spalten, die aus der Liste genommen werden sollen. Werte bleiben erhalten. */
  entfalleneSpalten?: string[];

  /**
   * Ids entfallener Messstellen, die auf ausdrücklichen Wunsch samt Prüfpunkten,
   * Protokoll und Fotos gelöscht werden. Nur einzeln angehakte; „alle auswählen“
   * hakt sie bewusst nicht an.
   */
  entfalleneMessstellen?: number[];
}

// ── Anzeigename einer Messstelle ────────────────────────────────────────────

/**
 * Sprechender Name einer Messstelle, aus ihren Werten gebildet.
 *
 * Die Kennzeichnung benennt eine Messstelle eindeutig und gewinnt deshalb; erst danach
 * kommen die Freitexte. Ohne jeden Inhalt bleibt die Excel-Zeile als
 * Notbehelf – auffindbar ist die Messstelle damit immer noch.
 *
 * Spiegelt die Namensbildung im Backend (IbnProtokollService), damit die
 * Auswahl im Export-Dialog denselben Namen zeigt wie das erzeugte PDF.
 */
export function messstelleAnzeigeName(
  values: { [column: string]: string } | null | undefined,
  fieldMapping: { [standardFeld: string]: string } | null | undefined,
  rowNumber: number
): string {
  const werte = values ?? {};
  const mapping = fieldMapping ?? {};

  const feldwert = (standardFeld: string): string => {
    for (const feld of messstelleFeldMitAliassen(standardFeld)) {
      const gemappt = mapping[feld];
      const wert = (gemappt ? werte[gemappt] : null) ?? werte[feld];
      if (wert && wert.trim()) {
        return wert.trim();
      }
    }
    return '';
  };

  return feldwert(FELD_KENNZEICHNUNG)
    || feldwert('Kurztext')
    || feldwert('Langtext')
    || `Zeile ${rowNumber}`;
}


// ═══════════════════════════════════════════════════════════════════════
// MANUELL ERSTELLTE LISTEN: SPALTENSTRUKTUR UND VORLAGEN
// ═══════════════════════════════════════════════════════════════════════

/**
 * Datentyp einer selbst definierten Spalte. Bestimmt Eingabe und Prüfung;
 * gespeichert wird – wie bei importierten Listen – immer Text.
 */
export type SpaltenDatentyp = 'Text' | 'Zahl' | 'Datum' | 'JaNein';

export const SPALTEN_DATENTYPEN: { wert: SpaltenDatentyp; text: string; icon: string }[] = [
  { wert: 'Text', text: 'Text', icon: 'text_fields' },
  { wert: 'Zahl', text: 'Zahl', icon: 'pin' },
  { wert: 'Datum', text: 'Datum', icon: 'event' },
  { wert: 'JaNein', text: 'Ja/Nein', icon: 'check_box' }
];

export function spaltenDatentypText(typ: SpaltenDatentyp | null | undefined): string {
  return SPALTEN_DATENTYPEN.find(t => t.wert === typ)?.text ?? 'Text';
}

/**
 * Eine Spalte einer manuell erstellten Liste bzw. Vorlage.
 *
 * `id` ist der stabile technische Schlüssel (bleibt bei Umbenennung gleich);
 * `name` ist zugleich der Schlüssel in `values` der Messstellen – genau wie
 * bei einer Excel-Spalte. Spiegelt `ListenSpalteDefinition` im Backend.
 */
export interface ListenSpalte {
  /** Leer bei einer noch nicht gespeicherten Spalte – der Server vergibt die Id. */
  id: string;
  name: string;
  datentyp: SpaltenDatentyp;
  pflicht: boolean;
  reihenfolge: number;
  /** Genau eine Spalte je Struktur. Immer Pflicht, je Liste eindeutig. */
  istKennzeichnung: boolean;
}

/** Spaltenstruktur einer manuell erstellten Liste. Null bei Excel-Importen. */
export interface ListenStruktur {
  spalten: ListenSpalte[];
}

/** Gespeicherte Struktur zum Wiederverwenden – ohne Messstellen, ohne Prüfungen. */
export interface ListenStrukturVorlage {
  id: number;
  name: string;
  beschreibung?: string | null;
  listenTyp: ListenTyp;
  kennzeichnungsArt: KennzeichnungsArt;
  /** Name der Kennzeichnungsspalte. */
  kennzeichnungLabel: string;
  struktur: ListenStruktur;
  spaltenCount: number;
  erstelltAmUtc: string;
  erstelltVon?: string | null;
  geaendertAmUtc?: string | null;
  geaendertVon?: string | null;
}

/** Antwort des Servers (HTTP 409), wenn eine Strukturänderung Bestätigung braucht. */
export interface StrukturAenderungAbgelehnt {
  message: string;
  loeschungBestaetigen: { id: string; name: string; anzahlWerte: number }[];
  kennzeichnungProbleme: string[];
}

/** Spalten in Anzeigereihenfolge. */
export function strukturSpalten(struktur: ListenStruktur | null | undefined): ListenSpalte[] {
  return [...(struktur?.spalten ?? [])].sort((a, b) => a.reihenfolge - b.reihenfolge);
}

/** Die Kennzeichnungsspalte der Struktur, falls vorhanden. */
export function kennzeichnungsSpalte(struktur: ListenStruktur | null | undefined): ListenSpalte | null {
  return strukturSpalten(struktur).find(s => s.istKennzeichnung) ?? null;
}

/**
 * Technische Felder, die eine benutzerdefinierte Spalte nicht überschreiben
 * darf – spiegelt `ListenStrukturRegeln.ReservierteNamen`.
 */
export const RESERVIERTE_SPALTENNAMEN: string[] = [
  'Id', 'RowNumber', 'Version', 'KategorieId', 'Kategorie', 'AnlagenteilId', 'Anlagenteil',
  'MessstellenListeId', 'ValuesJson', 'Pruefergebnis', 'PruefergebnisBemerkung', 'SignalStatus', 'UpdatedAtUtc'
];

export function istReservierterSpaltenname(name: string | null | undefined): boolean {
  const t = (name ?? '').trim();
  if (!t) {
    return false;
  }
  return t.startsWith('__')
    || RESERVIERTE_SPALTENNAMEN.some(r => r.toLowerCase() === t.toLowerCase());
}

/**
 * Prüft eine Struktur wie der Server (`ListenStrukturRegeln.ValidiereUndNormalisiere`):
 * mindestens eine Spalte, eindeutige Namen, keine internen Felder, genau eine
 * Kennzeichnung. Liefert die Fehlermeldungen; leer = gültig.
 */
export function pruefeStruktur(struktur: ListenStruktur | null | undefined): string[] {
  const fehler: string[] = [];
  const spalten = struktur?.spalten ?? [];

  if (spalten.length === 0) {
    return ['Bitte mindestens eine Spalte definieren.'];
  }

  const gesehen = new Set<string>();

  for (const spalte of spalten) {
    const name = (spalte.name ?? '').trim();

    if (!name) {
      fehler.push('Jede Spalte braucht einen Namen.');
      continue;
    }

    if (istReservierterSpaltenname(name)) {
      fehler.push(`„${name}“ ist ein internes Feld und kann nicht als Spaltenname verwendet werden.`);
    }

    const schluessel = name.toLowerCase();

    if (gesehen.has(schluessel)) {
      fehler.push(`Der Spaltenname „${name}“ kommt mehrfach vor.`);
    }

    gesehen.add(schluessel);
  }

  const kennzeichnungen = spalten.filter(s => s.istKennzeichnung).length;

  if (kennzeichnungen === 0) {
    fehler.push('Bitte genau eine Spalte als Kennzeichnung festlegen.');
  } else if (kennzeichnungen > 1) {
    fehler.push('Es darf nur eine Spalte als Kennzeichnung festgelegt sein.');
  }

  return Array.from(new Set(fehler));
}

/**
 * Prüft EINEN Wert gegen den Datentyp seiner Spalte – dieselben Regeln wie
 * `ListenStrukturWerte.PruefeWert` im Backend. Leer ist immer gültig.
 * Liefert die Fehlermeldung oder null.
 */
export function pruefeSpaltenWert(spalte: ListenSpalte, wert: string | null | undefined): string | null {
  const t = (wert ?? '').trim();

  if (!t) {
    return null;
  }

  switch (spalte.datentyp) {
    case 'Zahl': {
      const deutsch = t.replace(/\./g, '').replace(',', '.');
      return isNaN(Number(deutsch)) && isNaN(Number(t))
        ? `„${spalte.name}“ muss eine Zahl sein.`
        : null;
    }
    case 'Datum':
      return parseDatum(t) ? null : `„${spalte.name}“ muss ein Datum sein (TT.MM.JJJJ).`;
    case 'JaNein':
      return normalisiereJaNein(t) ? null : `„${spalte.name}“ muss Ja oder Nein sein.`;
    default:
      return null;
  }
}

/** „ja“/„x“/„true“ → 'Ja', „nein“/„0“ → 'Nein', sonst null. */
export function normalisiereJaNein(wert: string | null | undefined): 'Ja' | 'Nein' | null {
  const t = (wert ?? '').trim().toLowerCase();
  if (['ja', 'j', 'yes', 'y', 'true', '1', 'x', 'wahr'].includes(t)) {
    return 'Ja';
  }
  if (['nein', 'n', 'no', 'false', '0', 'falsch', '-'].includes(t)) {
    return 'Nein';
  }
  return null;
}

/** Liest TT.MM.JJJJ oder JJJJ-MM-TT; null, wenn kein Datum. */
export function parseDatum(wert: string | null | undefined): Date | null {
  const t = (wert ?? '').trim();

  let m = /^(\d{1,2})\.(\d{1,2})\.(\d{2}|\d{4})$/.exec(t);
  if (m) {
    const jahr = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
    const d = new Date(jahr, Number(m[2]) - 1, Number(m[1]));
    return d.getFullYear() === jahr && d.getMonth() === Number(m[2]) - 1 && d.getDate() === Number(m[1]) ? d : null;
  }

  m = /^(\d{4})-(\d{2})-(\d{2})/.exec(t);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return d.getFullYear() === Number(m[1]) && d.getMonth() === Number(m[2]) - 1 && d.getDate() === Number(m[3]) ? d : null;
  }

  return null;
}

/** Gespeicherte Form eines Datums (TT.MM.JJJJ) als JJJJ-MM-TT für `<input type="date">`. */
export function datumAlsIso(wert: string | null | undefined): string {
  const d = parseDatum(wert);
  if (!d) {
    return '';
  }
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** JJJJ-MM-TT aus `<input type="date">` in die gespeicherte Form TT.MM.JJJJ. */
export function isoAlsDatum(iso: string | null | undefined): string {
  const d = parseDatum(iso);
  if (!d) {
    return (iso ?? '').trim();
  }
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${dd}.${mm}.${d.getFullYear()}`;
}
